/**
 * The map as a shareable picture: base image, one disc per place with its
 * count, and the same header/footer treatment as the list card. Reuses the
 * list renderer's palette and helpers so the two pictures read as siblings.
 *
 * The base image must be same-origin (it is served from public/), otherwise
 * drawing it taints the canvas and toBlob refuses — which would silently break
 * share and copy while the preview still looked fine.
 */

import { FONT_BODY, FONT_MONO, colors, ellipsize, roundedRect } from './renderCard';

export interface MapCardPin {
  title: string;
  u: number;
  v: number;
  done: number;
  total: number;
}

export interface MapCardInput {
  title: string;
  subtitle: string;
  footer: string;
  attribution: string;
  image: string;
  map: { width: number; height: number };
  pins: MapCardPin[];
  done: number;
  total: number;
  placesDone: number;
}

const WIDTH = 1024;
const HEADER = 92;
const FOOTER = 30;
const PAD = 24;
const SCALE = 2;
const AMBER = '#ffb643';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load ${src}`));
    img.src = src;
  });
}

export async function renderMapCard(input: MapCardInput): Promise<Blob> {
  const c = colors();
  const img = await loadImage(input.image);

  const mapW = WIDTH;
  const mapH = Math.round((input.map.height / input.map.width) * WIDTH);
  const height = HEADER + mapH + FOOTER;

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH * SCALE;
  canvas.height = height * SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D is not available');
  ctx.scale(SCALE, SCALE);
  ctx.textBaseline = 'middle';

  ctx.fillStyle = c.bg;
  ctx.fillRect(0, 0, WIDTH, height);

  // ---- header ----
  const right = WIDTH - PAD;
  ctx.fillStyle = c.pip;
  ctx.font = `bold 13px ${FONT_MONO}`;
  ctx.textAlign = 'left';
  ctx.fillText('🐾 MeowTracker', PAD, 26);
  ctx.fillStyle = c.text;
  ctx.font = `bold 22px ${FONT_BODY}`;
  ctx.fillText(ellipsize(ctx, input.title, WIDTH * 0.6), PAD, 52);
  ctx.fillStyle = c.textDim;
  ctx.font = `13px ${FONT_BODY}`;
  ctx.fillText(ellipsize(ctx, input.subtitle, WIDTH * 0.6), PAD, 74);

  const percent = input.total === 0 ? 0 : Math.round((input.done / input.total) * 100);
  ctx.textAlign = 'right';
  ctx.fillStyle = c.pip;
  ctx.font = `bold 28px ${FONT_MONO}`;
  ctx.fillText(`${input.done} / ${input.total}`, right, 48);
  ctx.fillStyle = c.textDim;
  ctx.font = `13px ${FONT_MONO}`;
  ctx.fillText(`${percent}% · ${input.placesDone} / ${input.pins.length}`, right, 74);
  ctx.textAlign = 'left';

  // ---- map ----
  ctx.drawImage(img, 0, HEADER, mapW, mapH);
  // A light veil so the discs and their numbers stay legible on busy terrain.
  ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
  ctx.fillRect(0, HEADER, mapW, mapH);

  const R = 11;
  ctx.font = `bold 9px ${FONT_MONO}`;
  ctx.textAlign = 'center';
  for (const pin of input.pins) {
    const x = pin.u * mapW;
    const y = HEADER + pin.v * mapH;
    const all = pin.total > 0 && pin.done === pin.total;
    const some = !all && pin.done > 0;

    ctx.beginPath();
    ctx.arc(x, y, R, 0, Math.PI * 2);
    ctx.fillStyle = all ? c.pip : some ? c.surface : 'rgba(18, 23, 18, 0.85)';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = all ? c.pip : some ? AMBER : c.textFaint;
    ctx.stroke();

    ctx.fillStyle = all ? c.bg : some ? AMBER : c.textDim;
    ctx.fillText(`${pin.done}/${pin.total}`, x, y + 0.5);
  }
  ctx.textAlign = 'left';

  // ---- footer ----
  ctx.fillStyle = c.textFaint;
  ctx.font = `11px ${FONT_BODY}`;
  ctx.fillText(input.footer, PAD, height - FOOTER / 2);
  ctx.textAlign = 'right';
  ctx.fillText(ellipsize(ctx, input.attribution, WIDTH * 0.5), right, height - FOOTER / 2);
  ctx.textAlign = 'left';

  roundedRect(ctx, 1, 1, WIDTH - 2, height - 2, 4);
  ctx.strokeStyle = c.line;
  ctx.lineWidth = 2;
  ctx.stroke();

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('toBlob returned null'));
    }, 'image/png');
  });
}
